import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

/** Public gift viewer. Renders an animated, watermarked card or bouquet.
 *  Anti-screenshot deterrents applied:
 *  - Recipient name + sender name watermarked diagonally over the gift
 *  - Animated motion (CSS keyframes) so screenshots only capture a still
 *  - On-screen "Sent to: NAME on DATE via Obituary Relief" stamp
 *  - Right-click + drag-image disabled at the component level
 *  - User-select disabled (helps on mobile long-press)
 */
export default function GiftView() {
  const { token } = useParams();
  const [gift, setGift] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/gifts/view/${token}`)
      .then(({ data }) => setGift(data))
      .catch(e => setErr(e?.response?.data?.detail || "This gift cannot be opened."))
      .finally(() => setLoading(false));
  }, [token]);

  // Disable right-click + drag on the gift container
  const block = (e) => { e.preventDefault(); return false; };

  if (loading) return <div style={{ maxWidth: 480, margin: "120px auto", padding: 24 }}><div className="shimmer" style={{ height: 360, borderRadius: 18 }} /></div>;
  if (err) return (
    <div style={{ maxWidth: 480, margin: "120px auto", padding: 30, textAlign: "center" }}>
      <div className="serif" style={{ fontSize: 32, color: "var(--gold)" }}>Gift unavailable</div>
      <p className="muted" style={{ marginTop: 12 }}>{err}</p>
    </div>
  );
  if (!gift) return null;

  const isCard = gift.gift_type === "card";
  const w = gift.watermark || {};
  const issued = w.issued ? new Date(w.issued).toLocaleDateString() : "";

  return (
    <div data-testid="gift-view" style={{ minHeight: "100vh", background: "radial-gradient(circle at center, #1a1a22 0%, #050505 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", overflow: "hidden" }}>
      <style>{`
        @keyframes pulse-glow { 0%,100% { box-shadow: 0 0 60px 4px rgba(212,175,55,0.3); } 50% { box-shadow: 0 0 90px 8px rgba(212,175,55,0.6); } }
        @keyframes flame-flicker { 0%,100% { transform: scaleY(1) scaleX(1) translateY(0); opacity: 1; } 50% { transform: scaleY(1.08) scaleX(0.94) translateY(-1px); opacity: 0.92; } }
        @keyframes petal-sway { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(2deg); } }
        @keyframes shimmer-flow { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes float-rise { 0% { transform: translateY(0); opacity:0.4; } 100% { transform: translateY(-40px); opacity:0; } }
        .gift-stage { animation: pulse-glow 3.5s ease-in-out infinite; }
        .watermark-diag { position:absolute; inset:0; pointer-events:none; display:flex; align-items:center; justify-content:center; transform: rotate(-22deg); opacity:0.08; font-size: 38px; font-family: Georgia, serif; color: white; letter-spacing: 4px; }
        .gift-container { user-select:none; -webkit-user-select:none; -webkit-touch-callout:none; }
        .gift-container img { -webkit-user-drag: none; -webkit-touch-callout: none; pointer-events: none; }
      `}</style>

      <div style={{ fontSize: 11, letterSpacing: "0.4em", color: "#D4AF37", marginBottom: 14, opacity: 0.7 }}>FOR {(w.to || "you").toUpperCase()}</div>
      <div className="serif" style={{ fontSize: 22, color: "#bbb", marginBottom: 28, fontStyle: "italic" }}>from {w.from || "a friend"}</div>

      <div
        className="gift-stage gift-container"
        onContextMenu={block}
        onDragStart={block}
        style={{
          position: "relative",
          width: 360, height: 480,
          borderRadius: 18,
          background: isCard ? "linear-gradient(160deg,#7a1a2a 0%,#3d0e18 60%,#1a0508 100%)" : "linear-gradient(160deg,#1f3a1a 0%,#0e2114 60%,#050a06 100%)",
          padding: 32,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
          border: "1px solid rgba(212,175,55,0.4)",
        }}
      >
        {/* Watermark layer */}
        <div className="watermark-diag">{(w.to || "FRIEND").toUpperCase()} · OBITUARY RELIEF</div>

        {/* Animated centerpiece */}
        {isCard ? (
          <>
            <div style={{ fontSize: 120, lineHeight: 1, marginBottom: 12, animation: "petal-sway 4s ease-in-out infinite" }}>🕊</div>
            <div className="serif" style={{ fontSize: 28, color: "#fff", textAlign: "center", marginBottom: 6 }}>With deepest sympathy</div>
            <div style={{ height: 1, width: 60, background: "#D4AF37", margin: "10px auto" }} />
          </>
        ) : (
          <>
            <div style={{ position: "relative", marginBottom: 8 }}>
              <div style={{ fontSize: 130, lineHeight: 1, animation: "petal-sway 3.5s ease-in-out infinite" }}>💐</div>
              {/* Floating petals */}
              {[0,1,2,3].map(i => (
                <div key={i} style={{
                  position: "absolute",
                  left: 20 + i * 28, top: 60 + (i % 2) * 10,
                  fontSize: 16, color: "#ffb6c1",
                  animation: `float-rise ${3 + i * 0.4}s ease-out infinite`,
                  animationDelay: `${i * 0.5}s`,
                }}>✿</div>
              ))}
            </div>
            <div className="serif" style={{ fontSize: 26, color: "#fff", textAlign: "center" }}>In remembrance</div>
            <div style={{ height: 1, width: 60, background: "#D4AF37", margin: "10px auto" }} />
          </>
        )}

        {/* Sender message */}
        {gift.message && (
          <div style={{ color: "#e6e6e6", fontStyle: "italic", textAlign: "center", lineHeight: 1.6, fontSize: 14, padding: "0 12px", marginTop: 8 }}>
            &ldquo;{gift.message}&rdquo;
          </div>
        )}

        {/* Brand stamp at bottom */}
        <div style={{ position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center", fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: "0.2em" }}>
          OBITUARYRELIEF.COM
        </div>
      </div>

      {/* Anti-share watermark stamp under gift */}
      <div style={{ marginTop: 22, fontSize: 11, color: "#888", textAlign: "center", letterSpacing: "0.08em", lineHeight: 1.7 }}>
        Sent to <b style={{ color: "#bbb" }}>{w.to}</b> on <b style={{ color: "#bbb" }}>{issued}</b><br/>
        via Obituary Relief — this is a one-of-one personal gift
      </div>

      <a href="https://obituaryrelief.com" style={{ marginTop: 28, color: "#888", textDecoration: "none", fontSize: 12, borderBottom: "1px solid #444", paddingBottom: 2 }}>
        Send a gift of your own →
      </a>
    </div>
  );
}
